import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { productsApi } from '@/AdminPanel/api/products.api';
import type {
  IAdminLeasingProduct,
  IAdminLeasingProductItem,
  ICreateAdminLeasingProduct,
  IGetAdminLeasingProductsParams,
  TUpdateAdminLeasingProduct
} from '@/AdminPanel/pages/Products/types/product.api-types';
import { DictTypes } from '@/shared/constants';
import { getOptimizedDictionaries } from '@/store/dict.slice';

type TProductsSlice = {
  isLoading: boolean;
  products: IAdminLeasingProductItem[];
  total: number;
  product: IAdminLeasingProduct | null;
};

const initialState: TProductsSlice = {
  isLoading: false,
  products: [],
  total: 0,
  product: null
};

export const getProducts = createAsyncThunk(
  'admin/products',
  async (params: IGetAdminLeasingProductsParams) => {
    return await productsApi.getAdminProducts(params);
  }
);

export const createProduct = createAsyncThunk(
  'admin/products/create',
  async (data: ICreateAdminLeasingProduct, { dispatch }) => {
    const response = await productsApi.createAdminProduct(data);
    dispatch(
      getOptimizedDictionaries({ dictTypes: [DictTypes.LeasingProducts] })
    );
    return response;
  }
);

export const updateProduct = createAsyncThunk(
  'admin/products/update',
  async (
    {
      requestId,
      data
    }: {
      requestId: string;
      data: TUpdateAdminLeasingProduct;
    },
    { dispatch }
  ) => {
    const response = await productsApi.updateAdminProduct(requestId, data);
    dispatch(
      getOptimizedDictionaries({ dictTypes: [DictTypes.LeasingProducts] })
    );
    return response;
  }
);

const adminProductsSlice = createSlice({
  name: 'admin/products',
  initialState: initialState,
  reducers: {
    resetProductState(state) {
      state.product = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProducts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProducts.fulfilled, (state, action) => {
        state.products = action.payload.data;
        state.total = action.payload.total;
        state.isLoading = false;
      })
      .addCase(getProducts.rejected, (state) => {
        state.isLoading = false;
      });
  }
});

export const { resetProductState } = adminProductsSlice.actions;
export default adminProductsSlice;
